import { Button as GSButton, ButtonSpinner } from "@gluestack-ui/themed";
import { Text } from "@repo/core/components/core/Text";

import { ButtonIcon } from "./ButtonIcon";

import { ButtonProps } from "./types";
import { useRef, useState } from "react";
import { TextProps } from "@repo/core/components/core/Text/types";
import {
  getButtonTextColor,
  isPromiseLike,
  shouldHandleButtonPress
} from "@repo/core/components/core/Button/utils";

export const Button = ({
  text,
  isLoading,
  isDisabled,
  leftIcon,
  gap = "$2",
  rightIcon,
  variant,
  action,
  textStyle,
  onPress,
  throttleTime = 1,
  ...buttonProps
}: ButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPendingPress, setIsPendingPress] = useState(false);
  const lastPressAtRef = useRef<number | null>(null);
  const isPendingPressRef = useRef(false);

  const textColor = getButtonTextColor(action);
  const shouldShowLoading = Boolean(isLoading || isPendingPress);
  const handlePress: ButtonProps["onPress"] = (event) => {
    const currentPressAt = Date.now();

    if (
      !onPress ||
      isPendingPressRef.current ||
      !shouldHandleButtonPress(
        lastPressAtRef.current,
        currentPressAt,
        throttleTime * 1_000
      )
    ) {
      return;
    }

    lastPressAtRef.current = currentPressAt;
    const pressResult = onPress(event) as unknown;

    if (!isPromiseLike(pressResult)) {
      return;
    }

    isPendingPressRef.current = true;
    setIsPendingPress(true);

    const clearPendingPress = () => {
      isPendingPressRef.current = false;
      setIsPendingPress(false);
    };

    void Promise.resolve(pressResult).finally(clearPendingPress);
  };

  return (
    <GSButton
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
      {...buttonProps}
      onPress={handlePress}
      variant={variant}
      action={action}
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      isDisabled={shouldShowLoading || isDisabled}
      gap={gap}
      flexShrink={0}
      alignItems="center"
      justifyContent="center"
    >
      {shouldShowLoading ? <ButtonSpinner mr="$2" /> : null}
      {!shouldShowLoading && leftIcon?.name ? (
        <ButtonIcon
          {...leftIcon}
          color={isHovered ? leftIcon.hoverColor : leftIcon.color}
          mr="$2"
        />
      ) : null}
      {text ? (
        <Text
          {...(textStyle as TextProps)}
          color={textColor}
          ellipsizeMode="none"
        >
          {text}
        </Text>
      ) : null}
      {rightIcon?.name ? (
        <ButtonIcon
          {...rightIcon}
          color={isHovered ? rightIcon.hoverColor : rightIcon.color}
          ml="$2"
        />
      ) : null}
    </GSButton>
  );
};
